import type { LngLat } from './appState';

// Imperative interface for moving the map. The map-movement half is implemented
// by MapController and registered through createMapCommandQueue below; the
// centering flag lives in App state.
export interface MapCommands {
  // Jump to a point. Always re-triggers, even when the map is already there.
  panTo(center: LngLat, zoom?: number): void;
  // Fit the viewport to bounds ([[west, south], [east, north]]).
  fitBounds(bounds: number[][]): void;
  // Ring a point with the selection highlight (or clear it with null) — e.g. so a
  // searched water gets the same glow as a clicked pin.
  setSelectedPoint(coordinates: [number, number] | null): void;
  // Arm centering: the next detail card to load centers the map on its subject.
  centerOnNextCard(): void;
  // Disarm a pending centerOnNextCard (a card that centered, a map click, or a user zoom).
  cancelPendingCenter(): void;
}

// The map-movement half of MapCommands, implemented by MapController and
// registered when <Map /> mounts (null on unmount).
export type MapMovementCommands = Pick<MapCommands, 'panTo' | 'fitBounds' | 'setSelectedPoint'>;

export type RegisterMapCommands = (commands: MapMovementCommands | null) => void;

type QueuedCommand = (commands: MapMovementCommands) => void;

// Movement commands fired before the controller registers are buffered and
// replayed in order (commands can fire while <Map /> is hidden or still initializing).
// centerOnNextCard/cancelPendingCenter write shouldCenter directly and never queue.
export const createMapCommandQueue = (
  setShouldCenter: (shouldCenter: boolean) => void,
): { commands: MapCommands; register: RegisterMapCommands } => {
  let registered: MapMovementCommands | null = null;
  let pending: QueuedCommand[] = [];

  const run = (command: QueuedCommand) => {
    if (registered) {
      command(registered);
    } else {
      pending.push(command);
    }
  };

  return {
    commands: {
      panTo: (center, zoom) => {
        run((commands) => {
          commands.panTo(center, zoom);
        });
      },
      fitBounds: (bounds) => {
        run((commands) => {
          commands.fitBounds(bounds);
        });
      },
      setSelectedPoint: (coordinates) => {
        run((commands) => {
          commands.setSelectedPoint(coordinates);
        });
      },
      centerOnNextCard: () => {
        setShouldCenter(true);
      },
      cancelPendingCenter: () => {
        setShouldCenter(false);
      },
    },
    register: (commands) => {
      registered = commands;
      if (commands) {
        const queued = pending;
        pending = [];
        queued.forEach((command) => {
          command(commands);
        });
      }
    },
  };
};
